/**
 * Contrato del proveedor de pagos — agnóstico de la implementación (ADR-0004).
 * Dos rieles: tarjeta (MercadoPago, autorizar→capturar/anular, ADR-0002) y
 * offline (transferencia referenciada + conciliación bancaria, ADR-0003).
 * El paymentEngine depende de esta interfaz; se inyecta el mock o el live.
 */

export interface PaymentClient {
  /** Riel tarjeta · autoriza (retiene) el monto. `capture:false` en MP. */
  authorize(
    amount: number,
    currency: string,
    idempotencyKey: string,
  ): Promise<{ paymentId: string; status: 'authorized' | 'rejected' }>
  /** Riel tarjeta · captura (cobra) total o parcial. */
  capture(paymentId: string, amount: number): Promise<{ status: 'approved' }>
  /** Riel tarjeta · anula la retención (no se cobra). */
  voidAuthorization(paymentId: string): Promise<{ status: 'cancelled' }>
  /** Riel offline · emite una referencia única para la transferencia. */
  createOfflineReference(
    amount: number,
    currency: string,
    reservationId: string,
  ): Promise<{ reference: string }>
  /** Riel offline · concilia contra los movimientos bancarios por referencia. */
  matchMovement(reference: string, amount: number): Promise<{ matched: boolean }>
}

export interface MockPaymentOptions {
  /** Simula que la autorización de tarjeta fue rechazada. */
  rejectCard?: boolean
  /** Simula que la transferencia no apareció (sin match a las 24h). */
  offlineNoMatch?: boolean
}

let mockPay = 5000

/** Cliente de pagos de mentiritas — determinista, sin red. */
export class MockPaymentClient implements PaymentClient {
  constructor(private opts: MockPaymentOptions = {}) {}

  async authorize(
    _amount: number,
    _currency: string,
    _idempotencyKey: string,
  ): Promise<{ paymentId: string; status: 'authorized' | 'rejected' }> {
    mockPay += 1
    return { paymentId: `mp-${mockPay}`, status: this.opts.rejectCard ? 'rejected' : 'authorized' }
  }

  async capture(_paymentId: string, _amount: number): Promise<{ status: 'approved' }> {
    return { status: 'approved' }
  }

  async voidAuthorization(_paymentId: string): Promise<{ status: 'cancelled' }> {
    return { status: 'cancelled' }
  }

  async createOfflineReference(
    _amount: number,
    _currency: string,
    reservationId: string,
  ): Promise<{ reference: string }> {
    return { reference: `TC-${reservationId}-${mockPay}` }
  }

  async matchMovement(_reference: string, _amount: number): Promise<{ matched: boolean }> {
    return { matched: this.opts.offlineNoMatch !== true }
  }
}
